import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, unref as _unref, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "file-dropzone" }
const _hoisted_2 = { class: "content" }
const _hoisted_3 = { class: "title" }
const _hoisted_4 = { class: "subtitle" }
const _hoisted_5 = {
  key: 0,
  class: "file-list"
}
const _hoisted_6 = { class: "file-info" }
const _hoisted_7 = { class: "meta" }
const _hoisted_8 = { class: "name" }
const _hoisted_9 = { class: "size" }
const _hoisted_10 = { class: "actions" }
const _hoisted_11 = {
  key: 0,
  class: "loader"
}
const _hoisted_12 = ["onClick"]
const _hoisted_13 = {
  key: 0,
  class: "error"
}

import { ref } from "vue";
import type { Base64File, UploadFile } from "@/struct/FileTypes";
import { useI18n } from "vue-i18n";

/* -------- emits -------- */

export default /*@__PURE__*/_defineComponent({
  __name: 'BaseDrop',
  emits: ["update:files"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

/* -------- state -------- */
const files = ref<UploadFile[]>([]);
const isDragging = ref(false);
const error = ref<string | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const { t } = useI18n();

/* -------- icons -------- */
const DefaultIcon = {
  template: `<div class="icon-default">📎</div>`,
};

const DragIcon = {
  template: `<div class="icon-drag">➕</div>`,
};

/* -------- handlers -------- */
function openFileDialog() {
  fileInput.value?.click();
}

function onDragEnter() {
  isDragging.value = true;
}

function onDragLeave() {
  isDragging.value = false;
}

function onDrop(e: DragEvent) {
  isDragging.value = false;
  if (e.dataTransfer?.files) {
    handleFiles(Array.from(e.dataTransfer.files));
  }
}

function onFileSelect(e: Event) {
  const input = e.target as HTMLInputElement;
  if (input.files) {
    handleFiles(Array.from(input.files));
    input.value = "";
  }
}

/* -------- logic -------- */
async function handleFiles(selected: File[]) {
  error.value = null;

  for (const file of selected) {
    if (!isValid(file)) {
      error.value = t("base.drop.uploadError");
      continue;
    }

    const uploadFile: UploadFile = {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: "uploading",
    };

    files.value.push(uploadFile);

    try {
      const base64 = await fileToBase64(file);

      const index = files.value.findIndex((f) => f.id === uploadFile.id);
      if (index !== -1) {
        files.value[index] = {
          ...files.value[index],
          base64,
          status: "success",
        };
      }

      emitFiles();
    } catch {
      updateFile(uploadFile.id, {
        status: "error",
      });
    }
  }
}

function emitFiles() {
  const result: Base64File[] = files.value
    .filter((f) => f.status === "success" && f.base64)
    .map((f) => ({
      id: f.id,
      name: f.name,
      type: f.type,
      size: f.size,
      base64: f.base64!,
    }));

  emit("update:files", result);
}

function updateFile(id: string, patch: Partial<UploadFile>) {
  const index = files.value.findIndex((f) => f.id === id);
  if (index !== -1) {
    files.value[index] = {
      ...files.value[index],
      ...patch,
    };
  }
}

function removeFile(id: string) {
  files.value = files.value.filter((f) => f.id !== id);
  emitFiles();
}

function isValid(file: File) {
  const allowed = ["application/pdf", "image/png", "image/jpeg"];
  const maxSize = 10 * 1024 * 1024;
  return allowed.includes(file.type) && file.size <= maxSize;
}

function formatSize(bytes: number) {
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject();
  });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["drop-area", { 'is-dragging': isDragging.value }]),
      onDragenter: _withModifiers(onDragEnter, ["prevent"]),
      onDragover: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"])),
      onDragleave: _withModifiers(onDragLeave, ["prevent"]),
      onDrop: _withModifiers(onDrop, ["prevent"]),
      onClick: openFileDialog
    }, [
      _createElementVNode("input", {
        ref_key: "fileInput",
        ref: fileInput,
        type: "file",
        multiple: "",
        accept: ".pdf,.png,.jpg,.jpeg",
        tabindex: "-1",
        "aria-hidden": "true",
        onChange: onFileSelect,
        style: {"position":"absolute","width":"0","height":"0","opacity":"0","pointer-events":"none"}
      }, null, 544),
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(), _createBlock(_resolveDynamicComponent(isDragging.value ? DragIcon : DefaultIcon), { class: "icon" })),
        _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)("base.drop.title")), 1),
        _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)("base.drop.subtitle")), 1)
      ])
    ], 34),
    (files.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(files.value, (file) => {
            return (_openBlock(), _createElementBlock("div", {
              key: file.id,
              class: _normalizeClass(["file-item", file.status])
            }, [
              _createElementVNode("div", _hoisted_6, [
                _cache[1] || (_cache[1] = _createElementVNode("span", { class: "file-icon" }, "PDF", -1)),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, _toDisplayString(file.name), 1),
                  _createElementVNode("div", _hoisted_9, _toDisplayString(formatSize(file.size)), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_10, [
                (file.status === 'uploading')
                  ? (_openBlock(), _createElementBlock("span", _hoisted_11))
                  : (_openBlock(), _createElementBlock("button", {
                      key: 1,
                      class: "remove",
                      onClick: ($event: any) => (removeFile(file.id))
                    }, "✕", 8, _hoisted_12))
              ])
            ], 2))
          }), 128)),
          (error.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(error.value), 1))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})